/**
 * Defines some functions of general use by layout containers
 *
 * @module MW/layout/LayoutUtils
 * @copyright Copyright 2015-2017 The MathWorks, Inc.
 */
define([
    "dojo/_base/event",
    "dojo/_base/kernel",
    "dojo/_base/lang",
    "dojo/dom",
    "dojo/dom-geometry",
    "dojo/dom-style",
    "dojo/on",
    "dojo/sniff",
    "dojo/touch",
    "dijit/focus",
    "dijit/popup"
], function (event, kernel, lang, dom, domGeometry, domStyle, on, has, touch, focus, popup) {

    var preventDefault = function (node, event) {
        if (!node || event.target === node) {
            event.preventDefault();
        }
    };

    var stopEvent = function (e) {
        LayoutUtils.stopEvent(e);
    };

    var _animationEnabled = true;

    var LayoutUtils = {

        /**
         * Produces the CSS to realize a clip rect
         *
         * @param clipBox {{l: number, t: number, w: number, h: number}}
         * @returns {{clip: string}}
         */
        formatClipStyle:function (clipBox) {
            return {clip : "rect(" +
                clipBox.t + "px," +
               (clipBox.l + clipBox.w) + "px," +
               (clipBox.t + clipBox.h) + "px," +
                clipBox.l + "px)"};
        },

        /**
         * Returns true if the given DOM node is showing and its ancestors are showing
         *
         * @param node
         */
        isShowing: function (node) {
            return !!node && (node === document ||  // calling domStyle.get on document node causes problems.
                                                    // If the code has walked up to the document the starting node is showing
                (domStyle.get(node, "visibility") !== "hidden" && domStyle.get(node, "display") !== "none" &&
                 this.isShowing(node.parentNode)));
        },

        /**
         * Sets a widgets size, calling its resize method if available and otherwise settings its DOM node's
         * margin box.
         *
         * @param widget
         * @param box
         */
        setSize: function (widget, box) {
            if (widget.resize) {
                widget.resize(box);
            } else {
                domGeometry.setMarginBox(widget.domNode, box);
            }
        },

        restoreStyle: function (widget) {
            domStyle.set(widget.domNode, {
                top: "auto",
                left: "auto",
                position: "static",
                display: "block",
                clip: "auto"}
            );
        },

        /**
         * Finds the most immediate ancestor of the specified widget such that the ancestor if of the given class.
         * @param widget
         * @param targetClass
         */
        getAncestorOfClass: function (widget, targetClass) {
            var ancestor = widget.getParent();
            while (ancestor && !ancestor.isInstanceOf(targetClass)) {
                ancestor = ancestor.getParent();
            }
            return ancestor;
        },

        /**
         * Removes a node from its parent without destroying it (like dom-construct.destroy does).
         *
         * @param node
         */
        removeFromParent: function (node) {
            if (!node) return;
            var parent = node.parentNode;
            if (!parent) return;
            // This code is copied from dom-construct:destroy
            has("ie") && parent.canHaveChildren && "removeNode" in node ? node.removeNode(false) : parent.removeChild(node);
        },

        getScrollPosition: function (widget) {
            var scrollPosition = {l: 0, t: 0};
            var scrollPane = this._findScrollPane(widget);
            if (scrollPane) {
                scrollPosition.l = scrollPane.scrollLeft;
                scrollPosition.t = scrollPane.scrollTop;
            }
            return scrollPosition;
        },

        setScrollPosition: function (widget, position) {
            var scrollPane = this._findScrollPane(widget);
            if (scrollPane) {
                scrollPane.scrollLeft = position.l;
                scrollPane.scrollTop = position.t;
            }
        },

        /**
         * Finds the outermost scroll pane within the given widget and saves its position information in the given
         * properties object.  This method is intended for use in situations where a widget is removed from the
         * DOM hierarchy and then added back (perhaps in a different location).  Normally, this results in a loss
         * of scroll position.  This method along with the companion restoreScrollPosition can be used to preserve
         * the scroll position in such situations.
         *
         * @param widget
         * @param properties
         */
        saveScrollPosition: function (widget, properties) {
            if (!properties.scrollPane) {
                properties.scrollPane = this._findScrollPane(widget);
            }
            if (properties.scrollPane && !properties.savedScrollTop) {
                properties.savedScrollTop = properties.scrollPane.scrollTop;
                properties.savedScrollLeft = properties.scrollPane.scrollLeft;
            }
        },

        restoreScrollPosition: function (widget, properties) {
            if (properties.scrollPane) {
                if (properties.savedScrollTop) {
                    properties.scrollPane.scrollTop = properties.savedScrollTop;
                    delete properties.savedScrollTop;
                }
                if (properties.savedScrollLeft) {
                    properties.scrollPane.scrollLeft = properties.savedScrollLeft;
                    delete properties.savedScrollLeft;
                }
            }
        },

        _findScrollPane: function (widget) {
            var scrollPanes = dojo.query(".scrollPane", widget.domNode);
            if (scrollPanes.length > 0) {
                return scrollPanes[0];
            } else {
                return this._findScrolledChild(widget.domNode);
            }
        },

        _findScrolledChild: function (node) {
            if (node.scrollTop || node.scrollLeft) {
                return node;
            }
            if (!node.children) {
                return null;
            }
            var i, scrolledChild;
            for (i = 0; i < node.children.length; i++) {
                scrolledChild = this._findScrolledChild(node.children[i]);
                if (scrolledChild) {
                    return scrolledChild;
                }
            }
            return null;
        },

        /**
         * Saves the property from the given source, identified by the given key to a state object
         * but only if it does not equal the specified default value.
         *
         * Handling state information in this manner allows new defaults to be promulgated
         * to users who have not explicitly made changes.
         */
        saveIfNotDefault: function (state, key, source, defaultValue) {
            var value = source.get ? source.get(key) : source[key];
            if (value !== undefined && value !== defaultValue) {
                state[key] = value;
            }
        },

        restoreOrSetDefault: function (state, key, dest, defaultValue) {
            var value = state[key] === undefined ? defaultValue : state[key];
            if (dest.set) {
                dest.set(key, value);
            } else {
                dest[key] = value;
            }
        },

        /**
         * Prevents display of the browser context menu on the given node and its descendants
         *
         * @param node the node on which the default context menu should be blocked
         * @param {Boolean} includeDescendants true if context menu should be blocked on descendants as well (true by default)
         */
        preventDefaultContextMenu: function (node, includeDescentants) {
            on(node, "contextmenu", lang.partial(preventDefault, includeDescentants === false ? node : null));
        },

        /**
         * Prevents the given node from getting focus.
         *
         * @param node the node that should not get focus
         * @param hasPressListener true if there is already a listener that will stop press events on the node.
         */
        preventFocus: function (node, hasPressListener) {
            if (!hasPressListener) {
                on(node, touch.press, stopEvent);
            }

            // The previous approach was to set an attribute that tells the focus manager that this node should
            // not get focus.  Currently the focus manager doesn't actually prevent a focus change but rather reverses
            // it after the fact.  The problem is that it doesn't always manage to put it back in the right place
            // domAttr.set(node, "data-focus", "refuse");
        },

        /**
         * Stops an event unless a popup is showing.
         */
        stopEvent: function (e) {
            if (!this.isPopupShowing()) {
                event.stop(e);
            } else if (!this.popupHasFocus()) {
                // If a popup is showing the default event handling may close it.  If the popup doesn't have focus,
                // then focus shouldn't change.  If the default processing does change focus the following code will
                // put it back where it was (see geck 1527208).
                var focusNode = focus.curNode;
                setTimeout(function () {
                    focus.focus(focusNode);
                }, 0);
            }
        },

        isPopupShowing: function () {
            return popup._stack.length > 0;
        },

        popupHasFocus: function () {
            var popupNode = popup.getTopPopup().widget.domNode;
            return popupNode && (popupNode === focus.curNode || dom.isDescendant(focus.curNode, popupNode));
        },

        /**
         * Sets a CSS class when the locale is Chinese, Japanese or Korean.  This is used to display larger fonts
         * on some elements.
         *
         * This method should be moved into a web-widgets wide utilities module
         */
        setLocaleOnDomNode: function (node) {
            if (kernel.locale.indexOf("ko") === 0 || kernel.locale.indexOf("ja") === 0 || kernel.locale.indexOf("zh") === 0) {
                node.classList.add("isCJK");
            }
        },

        /**
         * Disables animation throughout the layout package.  For test purposes.
         */
        disableLayoutAnimation: function (){
            _animationEnabled = false;
        },

        /**
         * Reverses the effect of calling disableLayoutAnimation.  Note that animation within individual layout widgets may
         * still be governed by widget-specific "animationEnabled" settings.
         */
        enableLayoutAnimation: function () {
            _animationEnabled = true;
        },

        isAnimationEnabled: function () {
            return _animationEnabled;
        }
    };

    return LayoutUtils;
});